import React, { useEffect, useState } from "react";
import { Film, Save, EyeOff, Eye, RefreshCw, Upload } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

/**
 * FeaturedReelCard — admin card on /admin that manages the single
 * "featured reel" tile on the Landing homepage.
 *
 * Admin pastes a URL (Facebook / Instagram / TikTok / YouTube), a
 * title, an optional caption, optionally uploads a thumbnail (data
 * URL — compressed client-side), and toggles enabled. Save writes
 * to /api/admin/featured-reel. Live preview underneath the form
 * mimics what visitors see.
 */
const PLATFORMS = ["Facebook", "Instagram", "TikTok", "YouTube", "Other"];

async function fileToDataUrl(file, maxSide = 1024, quality = 0.75) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => { img.src = e.target.result; };
    reader.onerror = reject;
    img.onload = () => {
      let { width, height } = img;
      if (width > maxSide || height > maxSide) {
        const scale = Math.min(maxSide / width, maxSide / height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function FeaturedReelCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [state, setState] = useState({
    title: "",
    caption: "",
    platform: "Facebook",
    video_url: "",
    thumbnail_data_url: "",
    enabled: false,
    updated_at: null,
    updated_by: null,
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/featured-reel");
      setState({
        title: data.title || "",
        caption: data.caption || "",
        platform: data.platform || "Facebook",
        video_url: data.video_url || "",
        thumbnail_data_url: data.thumbnail_data_url || "",
        enabled: !!data.enabled,
        updated_at: data.updated_at || null,
        updated_by: data.updated_by || null,
      });
    } catch {
      toast.error("Could not load featured-reel settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!state.video_url || !/^https?:\/\//.test(state.video_url)) {
      toast.error("Video URL must start with http:// or https://");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/featured-reel", {
        title: state.title,
        caption: state.caption,
        platform: state.platform,
        video_url: state.video_url,
        thumbnail_data_url: state.thumbnail_data_url || null,
        enabled: state.enabled,
      });
      toast.success(state.enabled ? "Featured reel LIVE on the homepage" : "Featured reel saved (hidden)");
      setState((s) => ({ ...s, updated_at: data.updated_at, updated_by: data.updated_by }));
    } catch (e) {
      toast.error("Save failed — check the URL and try again");
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async () => {
    setSaving(true);
    try {
      await api.post("/admin/featured-reel", { enabled: !state.enabled });
      setState((s) => ({ ...s, enabled: !s.enabled }));
      toast.success(!state.enabled ? "Featured reel LIVE" : "Featured reel hidden");
    } catch {
      toast.error("Could not toggle");
    } finally {
      setSaving(false);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image too large — pick something under 8 MB");
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      setState((s) => ({ ...s, thumbnail_data_url: dataUrl }));
      toast.success("Thumbnail loaded — hit Save to publish");
    } catch {
      toast.error("Could not read that image");
    }
  };

  return (
    <section className="border hairline rounded-2xl p-5 sm:p-6 mb-6 bg-white" data-testid="admin-featured-reel-card">
      <header className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="eyebrow text-[#ff4500] flex items-center gap-1.5 mb-1">
            <Film className="w-3 h-3" /> Featured reel
          </div>
          <h2 className="font-display text-xl sm:text-2xl tracking-tight">Point the homepage at your latest reel</h2>
          <p className="text-xs text-[#666] mt-1 max-w-xl">
            Paste a Facebook / Instagram / TikTok / YouTube URL. Visitors see a big Landing tile with a
            play icon, title, caption and social-follow CTAs. Toggle Enabled to show or hide instantly.
          </p>
        </div>
        <button onClick={load} className="text-xs text-[#666] hover:text-[#ff4500] inline-flex items-center gap-1" data-testid="featured-reel-refresh">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </header>

      {loading ? (
        <div className="text-xs text-[#888]">Loading…</div>
      ) : (
        <div className="space-y-4">
          <label className="block">
            <div className="mono text-[10px] uppercase tracking-wider text-[#666] mb-1">Video URL *</div>
            <input
              type="url"
              value={state.video_url}
              onChange={(e) => setState((s) => ({ ...s, video_url: e.target.value }))}
              placeholder="https://www.facebook.com/share/r/..."
              className="w-full px-3 py-2 text-sm border border-[#e5e5e5] rounded-lg focus:outline-none focus:border-[#ff4500]"
              data-testid="featured-reel-url"
            />
          </label>

          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <div className="mono text-[10px] uppercase tracking-wider text-[#666] mb-1">Platform</div>
              <select
                value={state.platform}
                onChange={(e) => setState((s) => ({ ...s, platform: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-[#e5e5e5] rounded-lg focus:outline-none focus:border-[#ff4500]"
                data-testid="featured-reel-platform"
              >
                {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </label>
            <label className="block">
              <div className="mono text-[10px] uppercase tracking-wider text-[#666] mb-1">Title (headline)</div>
              <input
                type="text"
                value={state.title}
                onChange={(e) => setState((s) => ({ ...s, title: e.target.value }))}
                placeholder="e.g. 22,000 UK campsites, one tap away"
                className="w-full px-3 py-2 text-sm border border-[#e5e5e5] rounded-lg focus:outline-none focus:border-[#ff4500]"
                data-testid="featured-reel-title"
                maxLength={140}
              />
            </label>
          </div>

          <label className="block">
            <div className="mono text-[10px] uppercase tracking-wider text-[#666] mb-1">Caption (1-2 lines)</div>
            <textarea
              value={state.caption}
              onChange={(e) => setState((s) => ({ ...s, caption: e.target.value }))}
              placeholder="Optional hook that appears under the title on the homepage tile."
              rows={2}
              maxLength={400}
              className="w-full px-3 py-2 text-sm border border-[#e5e5e5] rounded-lg focus:outline-none focus:border-[#ff4500] resize-none"
              data-testid="featured-reel-caption"
            />
          </label>

          <div>
            <div className="mono text-[10px] uppercase tracking-wider text-[#666] mb-1">Thumbnail (optional, 9:16 works best)</div>
            <div className="flex items-center gap-3">
              {state.thumbnail_data_url && (
                <img src={state.thumbnail_data_url} alt="thumbnail preview" className="w-16 h-28 object-cover border hairline" />
              )}
              <label className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#e5e5e5] hover:border-[#ff4500] text-xs font-bold uppercase tracking-wider cursor-pointer">
                <Upload className="w-3 h-3" /> {state.thumbnail_data_url ? "Replace image" : "Upload image"}
                <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="featured-reel-thumbnail-upload" />
              </label>
              {state.thumbnail_data_url && (
                <button
                  onClick={() => setState((s) => ({ ...s, thumbnail_data_url: "" }))}
                  className="text-xs text-[#666] hover:text-[#ff4500]"
                >Remove</button>
              )}
            </div>
            <p className="text-[10px] text-[#888] mt-1">If left blank, the tile shows a bold orange gradient with a play icon.</p>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-2 border-t hairline">
            <button
              onClick={save}
              disabled={saving || !state.video_url}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#ff4500] hover:bg-[#e63e00] disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-bold uppercase tracking-wider shadow-sm transition-colors"
              data-testid="featured-reel-save"
            >
              <Save className="w-3 h-3" /> Save
            </button>
            <button
              onClick={toggleEnabled}
              disabled={saving || !state.video_url}
              className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-white text-xs font-bold uppercase tracking-wider shadow-sm transition-colors disabled:opacity-50 ${state.enabled ? "bg-[#666] hover:bg-[#333]" : "bg-[#1f7a35] hover:bg-[#176028]"}`}
              data-testid="featured-reel-toggle"
            >
              {state.enabled ? <><EyeOff className="w-3 h-3" /> Hide from homepage</> : <><Eye className="w-3 h-3" /> Show on homepage</>}
            </button>
            {state.updated_at && (
              <span className="text-[11px] text-[#888] mono ml-auto">
                Updated {new Date(state.updated_at).toLocaleString("en-GB")} by {state.updated_by || "admin"}
              </span>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
